import React from 'react';
import { Document, Image, Page, StyleSheet, Text, View } from '@react-pdf/renderer';

import logo from '../../../app/coop-logo.png';
import dayjs from 'dayjs';
import { formatNumber } from '../../billing/timesheets/BillingTools';

const css = StyleSheet.create({
    page: {
        color: '#212529',
        padding: '16pt',
        // fontFamily: 'Merriweather',
    },
    logo: {
        fontSize: '32pt',
        fontWeight: 'bold',
        color: '#339989',
        marginBottom: '8px',
    },
    header: {
        fontSize: '10pt'
    },
    category: {
        fontWeight: 'bold',
        marginBottom: '4px',
        marginTop: '8px',
        fontSize: '11pt',
    },
    description: {
        display: 'flex',
        justifyContent: 'flex-start',
    },
    row: {
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'space-between',
        fontSize: '8pt',
    },
    cell: {
        flex: 1,
        minWidth: '0px',
    },
    s1: {
        flex: 1
    },
    s2: {
        flex: 2
    },
    s3: {
        flex: 3
    },
    s4: {
        flex: 4
    },
    s5: {
        flex: 5
    },
    s6: {
        flex: 6
    },
    s7: {
        flex: 7
    },
    s8: {
        flex: 8
    },
    right: {
        display: 'flex',
        justifyContent: 'flex-end',
        textAlign: 'right',
    },
    left: {
        display: 'flex',
        justifyContent: 'flex-start'
    },
    label: {
        backgroundColor: '#cccccc',
        padding: '4px'
    }
})

const getItemDescription = item => {
    if (item.section === 'accommodation') return `Accommodation ${item.description}`;
    return item.description;
}

const PurchaseInvoicePDF = ({ member: { name = '' }, intermediary, referral, courtCase, quote, timesheet, submittedAt }) => {
    const borderStyle = '0.5px solid grey';
    const numberStyleSmall = { ...css.s1, ...css.right };
    const numberStyleLarge = { ...css.s3, ...css.right };

    const invoiceDate = submittedAt ? dayjs(submittedAt) : dayjs();
    const invoiceDueDate = invoiceDate.add(30, 'days');

    const headerLine = ({ lastRow = false, data }) => {

        return (
            <View style={{ ...css.row, borderTop: borderStyle, borderBottom: lastRow ? borderStyle : '' }}>
                <View style={{ flex: 2 }}>
                    <Text style={{ ...css.label }}>{data[0]}</Text>
                </View>
                <View style={{ flex: 4, }}>
                    <Text style={{ padding: '4px' }}>{data[1]}</Text>
                </View>
                {
                    data.length >= 3 &&
                    <View style={{ flex: 2 }}>
                        <Text style={{ ...css.label }}>{data[2]}</Text>
                    </View>
                }
                {
                    data.length >= 4 &&
                    <View style={{ flex: 4, borderRight: borderStyle }}>
                        <Text style={{ padding: '4px', }}>{data[3]}</Text>
                    </View>
                }
                {
                    data.length === 2 &&
                    <View style={{ flex: 6, borderRight: borderStyle }}>
                        <Text> </Text>
                    </View>
                }
            </View>
        );
    }

    const lineItems = quote.items()
        .filter(item => item.quantity > 0)
        .sort((a, b) => b.category.localeCompare(a.category));

    const lineCellStyle = {
        paddingTop: '3px',
        paddingRight: '3px',
        paddingBottom: '2px',
        paddingLeft: '3px',
        borderLeft: borderStyle,
        ...css.cell
    }

    const totalRowStyle = {
        ...css.row,
        justifyContent: 'flex-end'
    }

    const totalRow = ({ title, amount }) => {
        return (
            <View style={{ ...totalRowStyle }}>
                <View style={{ ...lineCellStyle, borderLeft: '', ...css.s1 }} >
                    <Text> </Text>
                </View>
                <View style={{ ...lineCellStyle, borderLeft: '', ...css.s2 }} >
                    <Text> </Text>
                </View>
                <View style={{ ...lineCellStyle, borderLeft: '', ...css.s4 }} >
                    <Text> </Text>
                </View>
                <View style={{ ...lineCellStyle, borderLeft: '', ...numberStyleSmall }} >
                    <Text> </Text>
                </View>
                <View style={{ ...lineCellStyle, borderLeft: '', ...numberStyleLarge }} >
                    <Text> </Text>
                </View>
                <View style={{ ...lineCellStyle, borderLeft: '', ...numberStyleLarge }} >
                    <Text></Text>
                </View>
                <View style={{ ...lineCellStyle, borderLeft: '', ...numberStyleSmall }} >
                    <Text></Text>
                </View>
                <View style={{ ...lineCellStyle, ...numberStyleLarge, borderBottom: borderStyle, backgroundColor: '#cccccc' }} >
                    <Text>{title}</Text>
                </View>
                <View style={{ ...lineCellStyle, ...numberStyleLarge, borderBottom: borderStyle, borderRight: borderStyle, backgroundColor: '#f2f2f2' }} >
                    <Text>{formatNumber(amount, 2)}</Text>
                </View>
            </View>
        );
    }

    return <Document title={`${referral.bookingReference} / ${courtCase.courtCaseNumber}`}>
        <Page size="A4" style={css.page}>
            <View style={{ ...css.row, alignItems: 'center', marginBottom: '16px' }}>
                <Image src={logo} style={{ flex: 6, justifyContent: 'flex-start', maxWidth: '100px', height: 'auto' }} />
                <View style={{ flex: 6, justifyContent: 'flex-end', }}>
                    <Text style={{ ...css.logo, ...css.right }}>MEMBER INVOICE</Text>
                </View>
            </View>
            <View style={{ marginBottom: '16px' }}>
                {headerLine({ data: ['Intermediary', name, 'Invoice #', quote.reference] })}
                {headerLine({ data: ['Address', '?', 'Invoice Date', invoiceDate.format('DD/MM/YYYY')] })}
                {headerLine({ data: ['Email', intermediary?.profile?.email, 'Booking Reference', referral.bookingReference] })}
                {headerLine({ data: ['Phone Number', intermediary?.profile?.phoneNumber, 'Payment Due', invoiceDueDate.format('DD/MM/YYYY')] })}
                {headerLine({ lastRow: true, data: ['VAT Reg No:', intermediary?.profile?.vatNumber || 'Not Applicable', 'Court Case #', timesheet.courtCaseNumber || courtCase.courtCaseNumber || ''] })}
            </View>
            <View style={{ marginBottom: '16px' }}>
                {headerLine({ data: ['Invoice To', 'The Intermediary Group Ltd'] })}
                {headerLine({ data: ['Address', 'Charter Buildings'] })}
                {headerLine({ data: [' ', '9 Ashton Lane, Sale M33 6WT'] })}
                {headerLine({ lastRow: true, data: ['Email', 'accounts@theintermediarycooperative.uk'] })}
            </View>

            <View style={{ ...css.row, borderTop: borderStyle, backgroundColor: '#cccccc' }}>
                <View style={{ ...lineCellStyle, ...css.s1 }} >
                    <Text>Line</Text>
                </View>
                <View style={{ ...lineCellStyle, ...css.s2 }} >
                    <Text>Date</Text>
                </View>
                <View style={{ ...lineCellStyle, ...css.s4 }} >
                    <Text>Description</Text>
                </View>
                <View style={{ ...lineCellStyle, ...numberStyleSmall }} >
                    <Text>Qty</Text>
                </View>
                <View style={{ ...lineCellStyle, ...numberStyleLarge }} >
                    <Text>Unit Price</Text>
                </View>
                <View style={{ ...lineCellStyle, ...numberStyleLarge }} >
                    <Text>Subtotal</Text>
                </View>
                <View style={{ ...lineCellStyle, ...numberStyleSmall }} >
                    <Text>VAT%</Text>
                </View>
                <View style={{ ...lineCellStyle, ...numberStyleLarge }} >
                    <Text>VAT Total</Text>
                </View>
                <View style={{ ...lineCellStyle, ...numberStyleLarge, borderRight: borderStyle }} >
                    <Text >Total (incl VAT)</Text>
                </View>
            </View>
            {
                lineItems.map((item, index) => {
                    const last = index + 1 === lineItems.length;
                    return <View style={{ ...css.row, borderTop: borderStyle, borderBottom: last ? borderStyle : '', }} key={item.key}>
                        <View style={{ ...lineCellStyle, ...css.s1 }} >
                            <Text>{index + 1}</Text>
                        </View>
                        <View style={{ ...lineCellStyle, ...css.s2 }} >
                            <Text>{new dayjs(item.date).format('DD/MM/YYYY')}</Text>
                        </View>
                        <View style={{ ...lineCellStyle, ...css.s4 }} >
                            <Text>{getItemDescription(item)}</Text>
                        </View>
                        <View style={{ ...lineCellStyle, ...numberStyleSmall }} >
                            <Text>{item.quantity}</Text>
                        </View>
                        <View style={{ ...lineCellStyle, ...numberStyleLarge }} >
                            <Text>{formatNumber(item.rate, 2)}</Text>
                        </View>
                        <View style={{ ...lineCellStyle, ...numberStyleLarge }} >
                            <Text>{formatNumber(item.amount, 2)}</Text>
                        </View>
                        <View style={{ ...lineCellStyle, ...numberStyleSmall }} >
                            <Text>{formatNumber(quote.taxRate() * 100.0, 1)}%</Text>
                        </View>
                        <View style={{ ...lineCellStyle, ...numberStyleLarge }} >
                            <Text>{formatNumber(item.amount * quote.taxRate(), 2)}</Text>
                        </View>
                        <View style={{ ...lineCellStyle, ...numberStyleLarge, borderRight: borderStyle }} >
                            <Text >{formatNumber(item.amount * (1 + quote.taxRate()), 2)}</Text>
                        </View>

                    </View>;
                })
            }
            {totalRow({ title: 'Net Total', amount: quote.netTotal() })}
            {totalRow({ title: 'VAT Total', amount: quote.netTotal() * quote.taxRate() })}
            {totalRow({ title: 'TOTAL DUE (incl VAT)', amount: quote.grandTotal() })}
            {
                quote.items().filter(item => item.note).length > 0 &&
                <View style={{ marginTop: '16px' }}>
                    <Text style={{ fontWeight: 'bold', fontSize: '11pt' }}>Notes</Text>
                    {
                        quote.items().filter(item => item.note).map((item, index) => {
                            return <View style={{ ...css.row }} key={`note-${index}`}>
                                <Text style={{ ...css.cell, ...css.s4, marginTop: '2px' }}>{item.description}</Text>
                                <Text style={{ ...css.cell, ...css.s8, marginTop: '2px' }}>"{item.note}"</Text>
                            </View>
                        })
                    }
                </View>
            }
        </Page>
    </Document>
};

export default PurchaseInvoicePDF;